import React, {useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {request} from "../shared/httpInterceptor";
import {useCountryLang} from "../hooks/useCountryLang";
import {useQuery} from "@tanstack/react-query";
import {ICookieCategory, ICookiesInit} from "../interfaces/ICookiesInit";
import styled from "styled-components";
import {Form, Spinner} from "react-bootstrap";
import {useMessages} from "../hooks/useMessages";
import Button from "./Button";
import {useRouter} from "next/router";
import {getTheme} from "../shared/themeUtils";
import {getUserConsent, GOOGLE_CONSENT_PARAM} from "./ConsentManager";
import IndicatorCountryLang from "./header/IndicatorCountryLang";
import GoogleTag from "./GoogleTag";

const Content = styled.div`
  .category {
    margin-bottom: ${getTheme("space.2")};

    .section-heading {
      font-size: ${getTheme("fontSizes.large")};
    }

    .options {
      .option {
        display: flex;
        margin-bottom: ${getTheme("space.1")};

        .policy-title {
          font-size: ${getTheme("fontSizes.small")};
          font-weight: bold;
        }
      }
    }
  }
`

const FooterAction = styled.div`
  display: flex;
  width: 100%;

  button {
    flex-grow: 1;
    margin: 0 ${getTheme("space.0")};
  }

  @media (max-width: ${getTheme("breakpoints.0")}) {
    flex-direction: column;
    button {
      margin: ${getTheme("space.0")} 0;
    }
  }
`

const fetchInitCookies = async (countryLang: string | null) => {
  const res = await request({
    url: `/${countryLang}/cookies/init`,
    method: 'GET',
  })
  return res.data
}


function CookiesManager() {
  const countryLang = useCountryLang()
  const [show, setShow] = useState(false);
  const [extendedOptions, setExtendedOptions] = useState(false)
  const [acceptedPolicies, setAcceptedPolicies] = useState<number[]>([])
  const [isLoadingAcceptAll, setIsLoadingAcceptAll] = useState(false)
  const [isLoadingAcceptSelected, setIsLoadingAcceptSelected] = useState(false)
  const [isLoadingRejectAll, setIsLoadingRejectAll] = useState(false)
  const [arePoliciesStored, setArePoliciesStored] = useState(false)
  const router = useRouter();
  const messagesQuery = useMessages()

  const handleClose = () => {
    setShow(false);
    router.reload()
  }
  const handleShow = () => setShow(true);

  const cookiesInitQuery = useQuery<ICookiesInit>(["cookies-init", countryLang], () =>
    fetchInitCookies(countryLang), {
    enabled: countryLang != null,
    refetchInterval: false,
    refetchOnMount: false
  })

  const cleanUserConsent = () => {
    return localStorage.removeItem(GOOGLE_CONSENT_PARAM);
  };

  useEffect(() => {
    // fetch init cookies and show the modal based on the response
    if (cookiesInitQuery.data && cookiesInitQuery.data?.categories?.length > 0) {
      // we show the manager if server returns categories with policies
      setShow(true)
      cleanUserConsent();

      // push mandatory policies to accepted array
      const mandatoryPolicies: number[] = [];
      cookiesInitQuery.data.categories.map(category => {
        category.policies.map(policy => {
          if (policy.mandatory && acceptedPolicies.length == 0) {
            mandatoryPolicies.push(policy.id)
          }
        })
      })
      if (mandatoryPolicies.length > 0) {
        setAcceptedPolicies(mandatoryPolicies)
      }
    }
    // Check accepted/rejected policies for Google cookie const update
    else if (cookiesInitQuery.data) {
      const userConsent = getUserConsent();
      if (userConsent == null) {
        storePolicies(cookiesInitQuery.data.policies)
      }

      setArePoliciesStored(true);
    }
  }, [cookiesInitQuery.data])


  const handleAcceptAllCookies = async (countryLang: string | null, host?: string, cookies?: any) => {
    setIsLoadingAcceptAll(true)
    const res = await request({
      url: `/${countryLang}/cookies/acceptAll`,
      method: 'POST',
      host: host,
      cookies: cookies
    })
    if (res.data && res.data.title == "OK") {
      handleClose()
    }
    setIsLoadingAcceptAll(false)
  }

  const storeGoogleConsents = (ad_storage: boolean, ad_user_data: boolean, ad_personalization: boolean, analytics_storage: boolean) => {
    if (typeof window === 'undefined') {
      return
    }

    const consentData = {
      'ad_storage': ad_storage ? 'granted' : 'denied',
      'ad_user_data': ad_user_data ? 'granted' : 'denied',
      'ad_personalization': ad_personalization ? 'granted' : 'denied',
      'analytics_storage': analytics_storage ? 'granted' : 'denied',
    }

    // Convert the object to a JSON string
    const consentStr = JSON.stringify(consentData);

    // Store the JSON google consent string in local storage
    localStorage.setItem(GOOGLE_CONSENT_PARAM, consentStr)
  }


  const handleRejectAllCookies = async (countryLang: string | null, host?: string, cookies?: any) => {
    setIsLoadingRejectAll(true)
    const res = await request({
      url: `/${countryLang}/cookies/rejectAll`,
      method: 'POST',
      host: host,
      cookies: cookies
    })
    if (res.data && res.data.title == "OK") {
      handleClose()
    }
    setIsLoadingRejectAll(false)
  }

  const storePolicies = (policies: number[] | undefined) => {
    let adsPolicyAccepted = policies!=null && policies.includes(104); //104 is the google-adwords policy
    let analyticsPolicyAccepted = policies!=null && policies.includes(105); //105 is the google-analytics policy

    //Store consent policies on local storage
    storeGoogleConsents(adsPolicyAccepted, adsPolicyAccepted, adsPolicyAccepted, analyticsPolicyAccepted)
  }

  const handleAcceptSelectedCookies = async () => {
    setIsLoadingAcceptSelected(true)
    const body = {
      acceptedPoliciesIds: acceptedPolicies
    }
    const res = await request({
      url: `/${countryLang}/cookies/accept`,
      method: 'POST',
      data: body
    })
    if (res.data && res.data.title == "OK") {
      handleClose()
    }
    setIsLoadingAcceptSelected(false)
  }


  const withNewLines = (text: string) => {
    return text.split(".");
  }


  const handlePolicyToggle = (id: number) => {
    if (!acceptedPolicies.includes(id)) {
      setAcceptedPolicies([...acceptedPolicies, id])
    } else {
      const filtered = acceptedPolicies.filter(policy => policy != id)
      setAcceptedPolicies(filtered)
    }
  }

  const loadGoogleTag = () => {
    return (
      <>
        { arePoliciesStored && <GoogleTag/> }
      </>
    )
  }

  return (
    <>
      <Modal show={show}
             onHide={handleClose}
             keyboard={false}
             backdrop={"static"}>
        <Modal.Header>
          <Modal.Title style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
            {messagesQuery.data && messagesQuery.data.messages["cookie-consent-title"]}
            <IndicatorCountryLang/>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!extendedOptions && cookiesInitQuery.data && messagesQuery.data &&
            <Content>
              {messagesQuery.data.messages["cookies-short-text"] && <div>
                {withNewLines(messagesQuery.data.messages["cookies-short-text"]).map(line => {
                  if (line) {
                    return (
                      <div key={line}>
                        {line}.
                        <br/><br/>
                      </div>
                    )
                  }
                })}
              </div>}
              {!extendedOptions && <Button variant="secondary" className={"mb-3"} onClick={() => setExtendedOptions(true)}>
                {messagesQuery.data && messagesQuery.data.messages["policy-disclaimer.showAll"]}
              </Button>}
              <p
                className={"text-muted small mb-0"}>{messagesQuery.data.messages["policy-disclaimer.lastestUpdate"]}: {cookiesInitQuery.data.latestPolicyUpdate}</p>
            </Content>
          }

          {extendedOptions &&
            <Content>
              {cookiesInitQuery.data && cookiesInitQuery.data.categories.map((category: ICookieCategory) => {
                return (
                  <div key={category.id} className={"category"}>
                    <h3 className={"section-heading"}>{category.title}</h3>
                    <p className={"text-muted"}>{category.text}</p>
                    <div className="options">
                      {category.policies.map(policy => {
                        return (
                          <div key={policy.title} className="option">
                            <Form.Check checked={acceptedPolicies.includes(policy.id)}
                                        disabled={policy.mandatory}
                                        onChange={() => handlePolicyToggle(policy.id)}
                                        type="switch"/>
                            <span className={"policy-title"}>{policy.title}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </Content>
          }

        </Modal.Body>
        {messagesQuery.data &&
          <Modal.Footer>
            <FooterAction>
              {!isLoadingAcceptAll &&
                <Button variant="primary" onClick={() => handleAcceptAllCookies(countryLang)}>
                {messagesQuery.data.messages["policy-configurator.accept-all"]}
              </Button>
              }
              {isLoadingAcceptAll &&
                <Button variant="primary">
                  <span className={"p-0"}><Spinner animation="border" size="sm"/></span>
                </Button>
              }

              {extendedOptions && !isLoadingAcceptSelected &&
                <Button variant="secondary" onClick={handleAcceptSelectedCookies}>
                  {messagesQuery.data.messages["policy-configurator.accept-selected"]}
                </Button>
              }
              {extendedOptions && isLoadingAcceptSelected &&
                <Button variant="secondary">
                  <span className={"p-0"}><Spinner animation="border" size="sm"/></span>
                </Button>
              }

              {!extendedOptions && <Button variant="secondary" onClick={() => handleRejectAllCookies(countryLang)}>
                {!isLoadingRejectAll && messagesQuery.data.messages["policy-disclaimer.rejectAll"]}
                {isLoadingRejectAll && <Spinner animation="border" size="sm"/>}
              </Button>}
            </FooterAction>
          </Modal.Footer>
        }
      </Modal>
      { loadGoogleTag() }
    </>
  );
}

export default CookiesManager;